import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Eye, EyeOff, ArrowLeft, Mail, CheckCircle, Loader2, Crown, Zap, Shield, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plansArray = [
  { id: 'starter', name: 'Starter', monthlyPrice: 9.90, yearlyPrice: 99, icon: Zap, color: 'from-blue-500 to-cyan-500', features: ['50 Dokumente/Monat', 'KI-Analyse', 'Kanban-Board'] },
  { id: 'pro', name: 'Pro', monthlyPrice: 29, yearlyPrice: 290, icon: Crown, color: 'from-violet-500 to-purple-600', features: ['Unbegrenzte Dokumente', 'KI-Analyse & E-Mails', 'Steuerberater-Export', 'Kalender-Integration'] },
  { id: 'business', name: 'Business', monthlyPrice: 49, yearlyPrice: 490, icon: Shield, color: 'from-amber-500 to-orange-500', features: ['Alles aus Pro', 'Multi-User', 'Multi-Company', 'Priority Support'] }
];

const SignupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPlan = searchParams.get('plan') || 'pro';
  const initialBilling = searchParams.get('billing') || 'monthly';
  const [currentPlanIndex, setCurrentPlanIndex] = useState(plansArray.findIndex(p => p.id === initialPlan) || 1);
  const [isYearly, setIsYearly] = useState(initialBilling === 'yearly');
  
  const currentPlan = plansArray[currentPlanIndex];
  const currentPrice = isYearly ? currentPlan.yearlyPrice : currentPlan.monthlyPrice;
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerifyMessage, setShowVerifyMessage] = useState(false);
  const { signup, user, logout } = useAuth();
  const navigate = useNavigate();

  const nextPlan = () => {
    setCurrentPlanIndex((prev) => (prev + 1) % plansArray.length);
  };

  const prevPlan = () => {
    setCurrentPlanIndex((prev) => (prev - 1 + plansArray.length) % plansArray.length);
  };

  // If user is already logged in, show a message
  if (user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-violet-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Du bist bereits angemeldet
          </h1>
          
          <p className="text-gray-600 mb-6">
            Du bist als <span className="font-semibold">{user.email}</span> angemeldet.
          </p>
          
          {user.subscription_status !== 'active' ? (
            <div className="space-y-3">
              <Link to={`/checkout?plan=${currentPlan.id}`}>
                <Button className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700">
                  Weiter zur Bezahlung
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  logout();
                  window.location.reload();
                }}
              >
                Ausloggen & neues Konto erstellen
              </Button>
            </div>
          ) : (
            <Link to="/dashboard">
              <Button className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700">
                Zum Dashboard
              </Button>
            </Link>
          )}
        </motion.div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    setLoading(true);
    try {
      // Create account - store selected plan and billing period for after verification
      localStorage.setItem('selected_plan', currentPlan.id);
      localStorage.setItem('billing_period', isYearly ? 'yearly' : 'monthly');
      await signup(email, password, name);
      toast.success('Konto erstellt! Bitte bestätige deine E-Mail.');
      setShowVerifyMessage(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
      setLoading(false);
    }
  };

  // Show email verification message after signup
  if (showVerifyMessage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-violet-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Fast geschafft!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Wir haben eine Bestätigungs-E-Mail an <span className="font-semibold text-gray-900">{email}</span> gesendet.
          </p>
          
          <div className="bg-violet-50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3 text-left">
              <CheckCircle className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Danach geht es weiter zur Bezahlung.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-xl p-4 mb-6">
            <p className="text-sm text-violet-700 font-medium">Dein gewählter Plan:</p>
            <p className="text-lg font-bold text-violet-900">
              {currentPlan.name} - {currentPrice}€/{isYearly ? 'Jahr' : 'Monat'}
              {isYearly && <span className="text-sm font-normal ml-2 text-emerald-600">(Spare 2 Monate!)</span>}
            </p>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Keine E-Mail erhalten?{' '}
              <button 
                onClick={() => {
                  toast.info('Neue Bestätigungs-E-Mail wird gesendet...');
                }}
                className="text-violet-600 hover:underline font-medium"
              >
                Erneut senden
              </button>
            </p>
            
            <p className="text-xs text-gray-400">
              Prüfe auch deinen Spam-Ordner. Die E-Mail kann bis zu 2 Minuten dauern.
            </p>
          </div>
          
          {/* TEST MODE: Skip verification button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={async () => {
                try {
                  // Call backend to skip verification AND payment (test mode)
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/skip-to-onboarding`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ email: email })
                  });
                  
                  const data = await response.json();
                  
                  if (response.ok && data.success) {
                    // Store the token
                    localStorage.setItem('token', data.token);
                    toast.success('Testmodus aktiviert! Weiter zum Onboarding...');
                    // Go directly to onboarding
                    setTimeout(() => {
                      window.location.href = '/onboarding';
                    }, 500);
                  } else {
                    toast.error(data.detail || 'Fehler');
                  }
                } catch (error) {
                  console.error('Skip error:', error);
                  toast.error('Fehler: ' + error.message);
                }
              }}
              className="w-full py-3 px-4 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Alles überspringen → Onboarding (Testmodus)
            </button>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Überspringt E-Mail-Verifizierung UND Zahlung für Tests
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
          
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              JaySmart
            </h1>
          </div>

          <h2 className="text-3xl font-bold tracking-tight mb-2 text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Konto erstellen
          </h2>
          <p className="text-gray-600 mb-8">
            Starte kostenlos und automatisiere deine Dokumentenverwaltung.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Dein Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 text-gray-900 placeholder:text-gray-400"
                placeholder="Max Mustermann"
                data-testid="signup-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                E-Mail-Adresse
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 text-gray-900 placeholder:text-gray-400"
                placeholder="deine@email.de"
                data-testid="signup-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-white border-gray-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20 pr-12 text-gray-900 placeholder:text-gray-400"
                  placeholder="Mindestens 6 Zeichen"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-full font-medium shadow-lg shadow-indigo-500/25"
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Erstellen...
                  </span>
                ) : (
                  'Konto erstellen'
                )}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-xs text-gray-500 text-center">
            Mit der Registrierung akzeptierst du unsere{' '}
            <a href="#" className="text-indigo-600 hover:underline">AGB</a> und{' '}
            <a href="#" className="text-indigo-600 hover:underline">Datenschutzrichtlinie</a>.
          </p>

          <p className="mt-8 text-center text-sm text-gray-600">
            Bereits ein Konto?{' '}
            <Link to="/login" className="text-indigo-600 hover:underline font-medium">
              Jetzt anmelden
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Plan Slider */}
      <div className={`hidden lg:flex flex-1 bg-gradient-to-br ${currentPlan.color} items-center justify-center p-8 relative`}>
        <div className="max-w-sm w-full text-white">
          {/* Monthly/Yearly Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/20 rounded-full p-1 flex">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!isYearly ? 'bg-white text-gray-900' : 'text-white/80 hover:text-white'}`}
              >
                Monatlich
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${isYearly ? 'bg-white text-gray-900' : 'text-white/80 hover:text-white'}`}
              >
                Jährlich
                <span className="ml-1 text-xs text-emerald-500 font-bold">-17%</span>
              </button>
            </div>
          </div>

          {/* Plan Card with Navigation */}
          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={prevPlan}
              className="absolute -left-12 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Plan Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPlan.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <currentPlan.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-xs uppercase tracking-wider">Gewählter Plan</p>
                    <h3 className="text-2xl font-bold">{currentPlan.name}</h3>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="text-4xl font-bold">
                    {currentPrice} €
                    <span className="text-lg font-normal text-white/70">/{isYearly ? 'Jahr' : 'Monat'}</span>
                  </div>
                  {isYearly && (
                    <p className="text-emerald-300 text-sm mt-1">
                      Spare {Math.round(currentPlan.monthlyPrice * 12 - currentPlan.yearlyPrice)}€ pro Jahr!
                    </p>
                  )}
                  <p className="text-white/60 text-sm mt-2">30 Tage Geld-zurück-Garantie</p>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {currentPlan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                      <Check className="w-4 h-4 text-emerald-300" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>

            {/* Right Arrow */}
            <button
              onClick={nextPlan}
              className="absolute -right-12 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Plan Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {plansArray.map((plan, index) => (
              <button
                key={plan.id}
                onClick={() => setCurrentPlanIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${index === currentPlanIndex ? 'bg-white w-6' : 'bg-white/40'}`}
              />
            ))}
          </div>

          {/* Steps */}
          <div className="mt-8 pt-6 border-t border-white/20">
            <p className="text-sm text-white/70 mb-3">Nach der Registrierung:</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1"><span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-xs">1</span> E-Mail bestätigen</span>
              <span className="flex items-center gap-1"><span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-xs">2</span> Bezahlen</span>
              <span className="flex items-center gap-1"><span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-xs">3</span> Loslegen</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
